import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useCompanySuggestions} from '../../../../hooks';
import {CompanySuggestion} from '../../../../api-client/models/company';
import styles from './InnAutocomplete.module.scss';
import {cn} from '../../../../utils';

interface InnAutocompleteProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect?: (suggestion: CompanySuggestion) => void;
  wrapperClassName?: string;
}

const InnAutocomplete: React.FC<InnAutocompleteProps> = ({
  value,
  onChange,
  onSuggestionSelect,
  wrapperClassName,
  ...inputProps
}) => {
  const {suggestions} = useCompanySuggestions(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);

  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    setOpen(suggestions.length > 0);
    setActiveIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: CompanySuggestion) => {
      justSelectedRef.current = true;
      onChange(suggestion.inn);
      onSuggestionSelect?.(suggestion);
      setOpen(false);
    },
    [onChange, onSuggestionSelect]
  );

  const handleFocus = useCallback(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (suggestions.length > 0) setOpen(true);
  }, [suggestions.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [open, suggestions, activeIndex, selectSuggestion]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, wrapperClassName)}>
      <input
        {...inputProps}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && suggestions.length > 0 && (
        <ul className={styles.suggestionsDropdown} role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={s.inn}
              role="option"
              aria-selected={i === activeIndex}
              className={cn(styles.suggestionItem, i === activeIndex && styles.suggestionItemActive)}
              onMouseDown={() => selectSuggestion(s)}
            >
              <span className={styles.suggestionInn}>{s.inn}</span>
              <span className={styles.suggestionName}>{s.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default InnAutocomplete;
